import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  HrApiHrEmployeeDetailsGetRequest,
  HrApiHrEmployeeLaborLevelsEmployeeIdPutRequest,
  HrCacheKeys,
  SchedulerEmployerCacheKeys,
  useApi,
} from '..';

const updateEmployeeLaborLevels =
  (api: Api) =>
  async (
    hrApiHrEmployeeLaborLevelsEmployeeIdPutRequest: HrApiHrEmployeeLaborLevelsEmployeeIdPutRequest
  ) => {
    return api.hrApi
      .hrEmployeeLaborLevelsEmployeeIdPut(
        hrApiHrEmployeeLaborLevelsEmployeeIdPutRequest
      )
      .then((response) => response.data ?? {});
  };

export const useUpdateEmployeeLaborLevels = () => {
  const api = useApi();
  return useMutation(updateEmployeeLaborLevels(api));
};
export const useEmployersGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [SchedulerEmployerCacheKeys.Employers],
    queryFn: () =>
      api.hrApi
        .hrEmployerGet({})
        .then((response) => response?.data?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
export type EmployerResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useEmployersGet>['data']>
>;
export const useEmployeesGet = (body: HrApiHrEmployeeDetailsGetRequest) => {
  const api = useApi();
  const { isMultiFacility } = useAccessToken();
  return useQuery({
    queryKey: [SchedulerEmployerCacheKeys.Employees, body],
    queryFn: async () => {
      return api.hrApi
        .hrEmployeeDetailsGet(body)
        .then((response) => response.data.data ?? {});
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: (isMultiFacility && Boolean(body.facilityId)) || !isMultiFacility,
  });
};
type EmployeesResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useEmployeesGet>['data']>
>;
export type EmployeeType = NonNullable<
  UnwrapArray<EmployeesResponseType['list']>
>;
const disableEmployee = (api: Api) => (employeeId: number) => {
  return api.hrApi.hrEmployeeDetailsEmployeeIdDisablePut({
    employeeId,
  });
};
export const useDisableEmployee = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(disableEmployee(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([SchedulerEmployerCacheKeys.Employees])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const saveEmployeePro = (api: Api) => (employeeId: number) => {
  return api.hrApi.hrEmployeeIdSavePut({
    employeeId,
  });
};
export const useSaveEmployeePro = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(saveEmployeePro(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([HrCacheKeys.SCHEDULER_TAB_VISIBILITY])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const undoEmployeePro = (api: Api) => (employeeId: number) => {
  return api.hrApi.hrEmployeeIdUndoPut({
    employeeId,
  });
};
export const useUndoEmployeePro = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(undoEmployeePro(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([HrCacheKeys.SCHEDULER_TAB_VISIBILITY])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

const getPositionWithCoverageEmployee =
  (api: Api, employeeId: number) => async () => {
    return api.hrApi
      .hrPositionWithCoverageEmployeeIdGet({
        employeeId,
      })
      .then((response) => response?.data.data ?? {});
  };
export const useGetPositionWithCoverageEmployee = (employeeId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [HrCacheKeys.PositionCoverageEmployee, { employeeId }],
    queryFn: getPositionWithCoverageEmployee(api, employeeId),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
const getEmployeePublishedSchedulePattern =
  (api: Api) => async (employeeId: number) => {
    return api.hrApi
      .hrEmployeeScheduleEmployeeIdGet({ employeeId })
      .then((response) => response.data.data ?? {});
  };
export const useGetEmployeePublishedSchedulePattern = () => {
  const api = useApi();
  return useMutation(getEmployeePublishedSchedulePattern(api));
};
const getEmployeeScheduleDisabledPattern =
  (api: Api) => async (employeeId: number) => {
    return api.hrApi
      .hrEmployeeScheduleDisabledEmployeeIdGet({ employeeId })
      .then((response) => response.data ?? {});
  };
export const useGetEmployeeScheduleDisabledPattern = () => {
  const api = useApi();
  return useMutation(getEmployeeScheduleDisabledPattern(api));
};
const updateAllEmployeeLaborLevels = (api: Api) => async () => {
  return api.hrApi
    .hrAllEmployeesLaborLevelsPut({})
    .then((response) => response.data ?? {});
};
export const useUpdateAllEmployeeLaborLevels = () => {
  const api = useApi();
  return useMutation(updateAllEmployeeLaborLevels(api));
};
